import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { getContact, createContact, updateContact, deleteContact, getPays, getTypeProfils, getAntennesList, getQualifications, getTypeStatus, setAuthToken, uploadPhoto, getUploadsUrl, uploadDocument, getContactTypeDocuments, deleteDocument, getGammesBornes, getBornesDisponibles, assignBorne, unassignBorne, getTypeEquipements, getEquipements, addAccessoire, deleteAccessoire, addBinome, updateBinome, deleteBinome, getLienBinomes, getContactsList, getTarifsForContact, getTarifsStandard, getTarifsGrilles } from '../services/api';
import ActionMenu from '../components/ActionMenu';
import ConfirmModal from '../components/ConfirmModal';
import { toast } from 'react-toastify';
import Loading from '../components/Loading';
import SearchSelect from '../components/SearchSelect';
import { borneType } from '../utils/borneLabel';
import VehiculesList from '../components/VehiculesList';
import ContactTarifsModal from '../components/ContactTarifsModal';
import { listOverrides, fmtEuro } from '../components/TarifReadOnlyGrid';
import { Gate, usePerm } from '../lib/perms';
import { Tag as TagIcon } from 'lucide-react';
import './ContactForm.css';

const RENTRER_SELFIZEE_OPTIONS = [
  { value: 'prospection', label: 'Prospection Selfizee' },
  { value: 'contact', label: 'Contact entrant' },
  { value: 'connaissance', label: 'Connaissance' },
  { value: 'autre', label: 'Autre' }
];

function WysiwygField({ value, onChange, rows = 3 }) {
  const ref = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    if (ref.current && !initialized.current) {
      ref.current.innerHTML = value || '';
      initialized.current = true;
    }
  }, [value]);

  const handleFormat = (command) => {
    document.execCommand(command, false, null);
    ref.current?.focus();
  };

  const handleInput = () => {
    onChange(ref.current?.innerHTML || '');
  };

  return (
    <div className="wysiwyg-field">
      <div className="wysiwyg-toolbar">
        <button type="button" onClick={() => handleFormat('bold')} title="Gras"><strong>B</strong></button>
        <button type="button" onClick={() => handleFormat('italic')} title="Italique"><em>I</em></button>
        <button type="button" onClick={() => handleFormat('underline')} title="Souligné"><u>U</u></button>
      </div>
      <div
        ref={ref}
        className="wysiwyg-editable"
        contentEditable
        onInput={handleInput}
        onBlur={handleInput}
        style={{ minHeight: `${rows * 1.6}em` }}
      />
    </div>
  );
}

function ContactForm() {
  const { id } = useParams();
  const navigate = useNavigate();
  const auth = useAuth();
  const [searchParams] = useSearchParams();
  const isEdit = Boolean(id);
  const fileInputRef = useRef(null);
  const vehiculesRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showTarifsModal, setShowTarifsModal] = useState(false);
  // Liste des lignes tarifaires modifiees pour ce contact vs le standard.
  // Calcule a la volee a partir des 2 endpoints tarifs (specifique vs standard).
  const [tarifOverrides, setTarifOverrides] = useState([]);
  const [tarifsReloadKey, setTarifsReloadKey] = useState(0);
  // Grilles proposables sur la fiche (cf. page Tarifs)
  const [grillesTarifaires, setGrillesTarifaires] = useState([]);
  // Confirmation de suppression : en modale, pour pouvoir dire ce qui se passe
  // réellement — la fiche part en corbeille, elle n'est pas détruite.
  const [confirmSuppression, setConfirmSuppression] = useState(false);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [activeStep, setActiveStep] = useState(1);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [showDocModal, setShowDocModal] = useState(false);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docFormData, setDocFormData] = useState({ file: null, type_document_id: '', nom: '' });
  const docFileRef = useRef(null);

  // Bornes state
  const [bornes, setBornes] = useState([]);
  const [showBorneModal, setShowBorneModal] = useState(false);
  const [bornesDisponibles, setBornesDisponibles] = useState([]);
  const [borneFormData, setBorneFormData] = useState({ gamme_borne_id: '', borne_id: '' });
  const [confirmUnassign, setConfirmUnassign] = useState(null); // borne object or null
  const [unassigning, setUnassigning] = useState(false);

  // Accessoires state
  const [accessoires, setAccessoires] = useState([]);
  const [showAccessoireModal, setShowAccessoireModal] = useState(false);
  const [equipementsList, setEquipementsList] = useState([]);
  const [accFormData, setAccFormData] = useState({ type_equipement_id: '', equipement_id: '', date_arrive: '', commentaire: '' });

  // Binomes state
  const [binomes, setBinomes] = useState([]);
  const [showBinomeModal, setShowBinomeModal] = useState(false);
  const [contactSearch, setContactSearch] = useState('');
  const [contactResults, setContactResults] = useState([]);
  const [searchingContacts, setSearchingContacts] = useState(false);
  const [binomeFormData, setBinomeFormData] = useState({ binome_id: '', binome_nom: '', lien_binome_id: '', precisions: '' });
  // Modification / suppression d'une liaison binôme existante
  const [editingBinome, setEditingBinome] = useState(null);
  const [editBinomeData, setEditBinomeData] = useState({ lien_binome_id: '', precisions: '' });
  const [deletingBinome, setDeletingBinome] = useState(null);
  const canEditBinome = usePerm('binomes.edit');
  const canDeleteBinome = usePerm('binomes.delete');
  const searchTimeout = useRef(null);

  const [lookups, setLookups] = useState({
    pays: [],
    typeProfils: [],
    antennes: [],
    qualifications: [],
    typeStatus: [],
    contactTypeDocuments: [],
    gammesBornes: [],
    typeEquipements: [],
    lienBinomes: []
  });

  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    civilite: '',
    date_naiss: '',
    tel_portable: '',
    tel_fixe: '',
    not_share_phone_number: false,
    email: '',
    situation_professionnelle: '',
    adresse: '',
    adresse_complementaire: '',
    cp: '',
    ville: '',
    pays_id: 5,
    addr_lat: '',
    addr_lng: '',
    distance: '',
    raison_sociale: '',
    siret: '',
    type_statu_id: '',
    etat: 'actif',
    antenne_id: searchParams.get('antenne_id') || '',
    type_profil_ids: [],
    antenne_principale: false,
    bon_savoir: '',
    information_facturation: '',
    tarif_mode: 'base',
    tarif_grille_id: null,
    contacts_lies: [],
    preference_livraison: '',
    vehicule: { type: '', couleur: '', immatriculation: '', commentaire: '' },
    prioritaire: false,
    qualification: '',
    linkedin: '',
    site_internet: '',
    photo_profil: '',
    id_trello: '',
    travaille_depuis: '',
    rentrer_selfizee: '',
    precisions_rentrer_selfizee: '',
    code_comptable_fournisseur: '',
    ne_plus_contacter: false,
    iban: '',
    bic: '',
    iban_added_in_edi: false,
    borne_ok: 0,
    nom_adresse: '',
    type_adresse: 'domicile',
    precisions_lieu: '',
    preferences_horaires_pickup_retrait: '',
    preferences_horaires_pickup_retour: '',
    accept_delivery: false,
    delivery_area: '',
    // Infos propres au contact (deplacees du tarif vers la fiche contact)
    competences: {},
    notations: {},
    stockage: {},
  });

  useEffect(() => {
    if (auth.user?.access_token) {
      setAuthToken(auth.user.access_token);
      loadLookups();
      getTarifsGrilles().then(setGrillesTarifaires).catch(() => setGrillesTarifaires([]));
      if (isEdit) {
        loadContact();
      } else {
        setLoading(false);
      }
    }
  }, [auth.user, id]);

  // Charge le tarif contact + standard et calcule la liste des lignes
  // modifiees. Recharge automatiquement quand on ferme la modale de tarif
  // (via tarifsReloadKey).
  useEffect(() => {
    if (!isEdit || !auth.user?.access_token || !id) return;
    Promise.all([
      getTarifsForContact(id).catch(() => ({ data: null, isCustom: false })),
      getTarifsStandard().catch(() => null),
    ]).then(([r, std]) => {
      if (r?.data && std) {
        setTarifOverrides(listOverrides(r.data, std));
      } else {
        setTarifOverrides([]);
      }
    });
  }, [auth.user, id, isEdit, tarifsReloadKey]);

  const loadLookups = async () => {
    try {
      const promises = [
        getPays(),
        getTypeProfils(true),
        getAntennesList(),
        getQualifications(),
        getTypeStatus().catch(() => [])
      ];
      if (isEdit) {
        promises.push(
          getContactTypeDocuments().catch(() => []),
          getGammesBornes().catch(() => []),
          getTypeEquipements().catch(() => []),
          getLienBinomes().catch(() => [])
        );
      }
      const results = await Promise.all(promises);
      setLookups({
        pays: results[0],
        typeProfils: results[1],
        antennes: results[2],
        qualifications: results[3],
        typeStatus: results[4],
        contactTypeDocuments: results[5] || [],
        gammesBornes: results[6] || [],
        typeEquipements: results[7] || [],
        lienBinomes: results[8] || []
      });
    } catch (error) {
      console.error('Error loading lookups:', error);
    }
  };

  const loadContact = async () => {
    try {
      const data = await getContact(id);
      // Get type_profil_ids from type_profils array if available
      const typeProfilIds = data.type_profils?.map(tp => tp.id) || (data.type_profil_id ? [data.type_profil_id] : []);

      setFormData({
        nom: data.nom || '',
        prenom: data.prenom || '',
        civilite: data.civilite || '',
        date_naiss: data.date_naiss?.split('T')[0] || '',
        tel_portable: data.tel_portable || '',
        tel_fixe: data.tel_fixe || '',
        not_share_phone_number: data.not_share_phone_number || false,
        email: data.email || '',
        situation_professionnelle: data.situation_professionnelle || '',
        adresse: data.adresse || '',
        adresse_complementaire: data.adresse_complementaire || '',
        cp: data.cp || '',
        ville: data.ville || '',
        pays_id: data.pays_id || 5,
        addr_lat: data.addr_lat || '',
        addr_lng: data.addr_lng || '',
        distance: data.distance || '',
        raison_sociale: data.raison_sociale || '',
        siret: data.siret || '',
        type_statu_id: data.type_statu_id || '',
        etat: data.etat || 'actif',
        // Normalise en string pour que le <select> matche les <option value={a.id}>
        // (sinon Number 42 vs option value "42" pouvait laisser le select sur "Sélectionner").
        antenne_id: data.antenne_id !== null && data.antenne_id !== undefined && data.antenne_id !== 0 ? String(data.antenne_id) : '',
        // Stocke la ville de l'antenne courante pour pouvoir l'afficher dans la
        // dropdown meme si l'antenne n'apparait pas dans getAntennesList() (filtree, etc.)
        __antenne_ville_fallback: data.antenne_ville || '',
        type_profil_ids: typeProfilIds,
        antenne_principale: data.antenne_principale || false,
        bon_savoir: data.bon_savoir || '',
        information_facturation: data.information_facturation || '',
        tarif_mode: data.tarif_mode === 'specifique' ? 'specifique' : 'base',
        tarif_grille_id: data.tarif_grille_id ?? null,
        contacts_lies: Array.isArray(data.contacts_lies) ? data.contacts_lies : [],
        preference_livraison: data.preference_livraison || '',
        vehicule: data.vehicule || { type: '', couleur: '', immatriculation: '', commentaire: '' },
        prioritaire: !!data.prioritaire,
        qualification: data.qualification || '',
        linkedin: data.linkedin || '',
        site_internet: data.site_internet || '',
        photo_profil: data.photo_profil || data.photo || '',
        id_trello: data.id_trello || '',
        travaille_depuis: data.travaille_depuis?.split('T')[0] || '',
        rentrer_selfizee: data.rentrer_selfizee || '',
        precisions_rentrer_selfizee: data.precisions_rentrer_selfizee || '',
        code_comptable_fournisseur: data.code_comptable_fournisseur || '',
        ne_plus_contacter: data.ne_plus_contacter || false,
        iban: data.iban || '',
        bic: data.bic || '',
        iban_added_in_edi: data.iban_added_in_edi || false,
        borne_ok: data.borne_ok || 0,
        nom_adresse: data.nom_adresse || '',
        // Contacts existants sans type : un nom de lieu renseigné ⇒ entreprise
        type_adresse: data.type_adresse || (data.nom_adresse ? 'entreprise' : 'domicile'),
        precisions_lieu: data.precisions_lieu || '',
        preferences_horaires_pickup_retrait: data.preferences_horaires_pickup_retrait || '',
        preferences_horaires_pickup_retour: data.preferences_horaires_pickup_retour || '',
        accept_delivery: data.accept_delivery || false,
        delivery_area: data.delivery_area || '',
        competences: data.competences || {},
        notations: data.notations || {},
        stockage: data.stockage || {}
      });

      // Load related data for edit mode
      setDocuments(data.documents || []);
      setBornes(data.bornes || []);
      setAccessoires(data.accessoires || []);
      setBinomes(data.binomes || []);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleTypeProfilChange = (e) => {
    const options = e.target.options;
    const selectedIds = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) {
        selectedIds.push(parseInt(options[i].value));
      }
    }
    setFormData(prev => ({
      ...prev,
      type_profil_ids: selectedIds
    }));
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploadingPhoto(true);
      const result = await uploadPhoto(file);
      setFormData(prev => ({
        ...prev,
        photo_profil: result.data.path
      }));
      toast.success('Photo uploadée');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingPhoto(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.nom || !formData.prenom) {
      toast.error('Le nom et prénom sont obligatoires');
      return;
    }

    try {
      setSaving(true);
      // Convert type_profil_ids to type_profil_id for backward compatibility
      const dataToSave = {
        ...formData,
        type_profil_id: formData.type_profil_ids[0] || null,
        photo: formData.photo_profil || '',
        // Le nom du lieu n'a de sens que pour une adresse d'entreprise
        nom_adresse: formData.type_adresse === 'entreprise' ? formData.nom_adresse : ''
      };

      if (isEdit) {
        await updateContact(id, dataToSave);
        toast.success('Contact mis à jour');
        navigate(`/contacts/${id}`);
      } else {
        const result = await createContact(dataToSave);
        const newId = result.data.id;
        // Si des véhicules ont été ajoutés avant la création, on les flushe
        try { await vehiculesRef.current?.flushPending(newId); } catch (_) { /* non bloquant */ }
        toast.success('Contact créé');
        navigate(`/contacts/${newId}`);
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // === Document handlers ===
  const handleAddDocument = async (e) => {
    e.preventDefault();
    if (!docFormData.file) { toast.error('Veuillez sélectionner un fichier'); return; }
    try {
      setUploadingDoc(true);
      const result = await uploadDocument(docFormData.file, id, docFormData.type_document_id || null, docFormData.nom || null);
      const newDoc = result.data || result;
      // Get type name from lookups
      const typeName = lookups.contactTypeDocuments.find(t => t.id === parseInt(docFormData.type_document_id))?.nom;
      setDocuments(prev => [{ ...newDoc, type_document_nom: typeName || 'Document' }, ...prev]);
      setShowDocModal(false);
      setDocFormData({ file: null, type_document_id: '', nom: '' });
      if (docFileRef.current) docFileRef.current.value = '';
      toast.success('Document ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploadingDoc(false);
    }
  };

  const handleDeleteDocument = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await deleteDocument(docId);
      setDocuments(prev => prev.filter(d => d.id !== docId));
      toast.success('Document supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // === Borne handlers ===
  // La gamme n'est qu'un filtre facultatif : sans gamme, toutes les bornes
  // disponibles sont proposées et on les retrouve par la recherche.
  const loadBornesDisponibles = async (gammeId) => {
    try {
      setBornesDisponibles(await getBornesDisponibles(gammeId || undefined));
    } catch { setBornesDisponibles([]); }
  };

  const openBorneModal = () => {
    setBorneFormData({ gamme_borne_id: '', borne_id: '' });
    setBornesDisponibles([]);
    setShowBorneModal(true);
    loadBornesDisponibles('');
  };

  const handleGammeChange = async (gammeId) => {
    setBorneFormData({ gamme_borne_id: gammeId, borne_id: '' });
    loadBornesDisponibles(gammeId);
  };

  const handleAssignBorne = async (e) => {
    e.preventDefault();
    if (!borneFormData.borne_id) { toast.error('Veuillez sélectionner une borne'); return; }
    try {
      await assignBorne(id, borneFormData.borne_id);
      // Reload contact data to get updated bornes
      const data = await getContact(id);
      setBornes(data.bornes || []);
      setShowBorneModal(false);
      setBorneFormData({ gamme_borne_id: '', borne_id: '' });
      setBornesDisponibles([]);
      toast.success('Borne assignée');
    } catch (error) {
      toast.error('Erreur lors de l\'assignation');
    }
  };

  const handleUnassignBorne = (borne) => {
    setConfirmUnassign(borne);
  };

  const confirmUnassignBorne = async () => {
    if (!confirmUnassign) return;
    const borneId = confirmUnassign.id;
    setUnassigning(true);
    try {
      await unassignBorne(id, borneId);
      setBornes(prev => prev.filter(b => b.id !== borneId));
      toast.success('Borne retirée');
      setConfirmUnassign(null);
    } catch (error) {
      toast.error('Erreur lors du retrait');
    } finally {
      setUnassigning(false);
    }
  };

  // === Accessoire handlers ===
  const handleTypeEquipementChange = async (typeId) => {
    setAccFormData(prev => ({ ...prev, type_equipement_id: typeId, equipement_id: '' }));
    if (typeId) {
      try {
        const equips = await getEquipements(typeId);
        setEquipementsList(equips);
      } catch { setEquipementsList([]); }
    } else {
      setEquipementsList([]);
    }
  };

  const handleAddAccessoire = async (e) => {
    e.preventDefault();
    if (!accFormData.type_equipement_id) { toast.error('Veuillez sélectionner un type'); return; }
    try {
      await addAccessoire(id, accFormData);
      // Reload
      const data = await getContact(id);
      setAccessoires(data.accessoires || []);
      setShowAccessoireModal(false);
      setAccFormData({ type_equipement_id: '', equipement_id: '', date_arrive: '', commentaire: '' });
      setEquipementsList([]);
      toast.success('Accessoire ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleDeleteAccessoire = async (accId) => {
    if (!window.confirm('Supprimer cet accessoire ?')) return;
    try {
      await deleteAccessoire(id, accId);
      setAccessoires(prev => prev.filter(a => a.id !== accId));
      toast.success('Accessoire supprimé');
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // === Binome handlers ===
  const handleContactSearch = (value) => {
    setContactSearch(value);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (value.length < 2) { setContactResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      try {
        setSearchingContacts(true);
        const results = await getContactsList({ search: value, exclude_id: id });
        setContactResults(results);
      } catch { setContactResults([]); }
      finally { setSearchingContacts(false); }
    }, 300);
  };

  const handleSelectBinomeContact = (contact) => {
    setBinomeFormData(prev => ({
      ...prev,
      binome_id: contact.id,
      binome_nom: `${contact.prenom} ${contact.nom}`
    }));
    setContactSearch('');
    setContactResults([]);
  };

  const handleAddBinome = async (e) => {
    e.preventDefault();
    if (!binomeFormData.binome_id) { toast.error('Veuillez sélectionner un contact'); return; }
    try {
      await addBinome(id, {
        binome_id: binomeFormData.binome_id,
        lien_binome_id: binomeFormData.lien_binome_id || null,
        precisions: binomeFormData.precisions || ''
      });
      // Reload
      const data = await getContact(id);
      setBinomes(data.binomes || []);
      setShowBinomeModal(false);
      setBinomeFormData({ binome_id: '', binome_nom: '', lien_binome_id: '', precisions: '' });
      toast.success('Binôme ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout');
    }
  };

  const handleEditBinome = (binome) => {
    setEditingBinome(binome);
    setEditBinomeData({
      lien_binome_id: binome.lien_binome_id || '',
      precisions: binome.precisions || ''
    });
  };

  const handleUpdateBinome = async (e) => {
    e.preventDefault();
    if (!editingBinome) return;
    try {
      await updateBinome(id, editingBinome.id, editBinomeData);
      const data = await getContact(id);
      setBinomes(data.binomes || []);
      setEditingBinome(null);
      toast.success('Liaison binôme mise à jour');
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const confirmDeleteBinome = async () => {
    if (!deletingBinome) return;
    try {
      await deleteBinome(id, deletingBinome.id);
      const data = await getContact(id);
      setBinomes(data.binomes || []);
      setDeletingBinome(null);
      toast.success('Liaison binôme supprimée');
    } catch (error) {
      toast.error('Erreur lors de la suppression de la liaison');
    }
  };

  if (loading) return <Loading />;

  const steps = [
    { id: 1, label: 'Contact' },
    { id: 2, label: 'Informations' },
    { id: 3, label: 'Documents' },
    { id: 4, label: 'Matériel' },
    { id: 5, label: 'Binôme(s)' }
  ];

  const hasLocation = formData.addr_lat && formData.addr_lng;
  const position = hasLocation ? [parseFloat(formData.addr_lat), parseFloat(formData.addr_lng)] : null;

  // Grilles qu'on propose au choix : les actives, plus celle que la fiche porte
  // déjà même désactivée (désactiver ne re-tarife personne). La grille de base
  // passe en tête, c'est le choix par défaut.
  const grillesProposables = grillesTarifaires
    .filter((g) => g.statut === 'actif' || g.id === formData.tarif_grille_id)
    .sort((a, b) => (b.par_defaut ? 1 : 0) - (a.par_defaut ? 1 : 0));
  const grilleAppliquee =
    grillesTarifaires.find((g) => (formData.tarif_grille_id ? g.id === formData.tarif_grille_id : g.par_defaut)) || null;
  const styleRadio = { display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#475569' };
  const styleRadioInput = { width: 'auto', padding: 0, margin: 0, accentColor: '#6366f1', cursor: 'pointer' };
  const styleMention = { fontStyle: 'normal', fontSize: 12, color: '#94a3b8' };

  return (
    <div id="contact-new" className="contact-form-page">
      <header className="page-header">
        <div className="header-info">
          <h1>Fiche contact antenne</h1>
        </div>
        <div className="header-actions">
          {isEdit && (
            <Gate perm="contacts.delete">
              <button
                type="button"
                className="btn btn-ghost"
                style={{ border: '1px solid #d9d9d9', color: '#676767' }}
                disabled={saving}
                onClick={() => setConfirmSuppression(true)}
              >
                Supprimer
              </button>
            </Gate>
          )}
          {isEdit && (
            <button type="button" className="btn btn-success" disabled={saving} onClick={handleSubmit}>
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </button>
          )}
          {/* En édition on revient à la fiche qu'on vient de quitter, pas à la liste. */}
          <Link to={isEdit ? `/contacts/${id}` : '/contacts'} className="btn btn-secondary">Quitter</Link>
        </div>
      </header>

      <div className="steps-nav">
        {steps.map(step => (
          <button
            key={step.id}
            className={`step ${activeStep === step.id ? 'active' : ''} ${step.id < activeStep ? 'completed' : ''}`}
            onClick={() => setActiveStep(step.id)}
          >
            {step.id} - {step.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="form-container">
        {activeStep === 1 && (
          <div className="step-content">
            <h2>État civil</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Prénom *</label>
                <input type="text" name="prenom" value={formData.prenom} onChange={handleChange} required />
              </div>
              <div className="form-group">
                <label>Nom *</label>
                <input type="text" name="nom" value={formData.nom} onChange={handleChange} required />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group form-group-sm">
                <label>Civilité</label>
                <select name="civilite" value={formData.civilite} onChange={handleChange}>
                  <option value="">-</option>
                  <option value="mr">M.</option>
                  <option value="m">Mme</option>
                  <option value="mlle">Mlle</option>
                </select>
              </div>
              <div className="form-group">
                <label>Date de naissance</label>
                <input type="date" name="date_naiss" value={formData.date_naiss} onChange={handleChange} />
              </div>
            </div>

            <h2>Coordonnées</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Téléphone portable</label>
                <input type="text" name="tel_portable" value={formData.tel_portable} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Téléphone fixe</label>
                <input type="text" name="tel_fixe" value={formData.tel_fixe} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row checkboxes">
              <label className="checkbox-label">
                <input type="checkbox" name="not_share_phone_number" checked={formData.not_share_phone_number} onChange={handleChange} />
                <span>Ne pas communiquer le portable aux clients</span>
              </label>
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Situation professionnelle <span className="label-hint">(ex: Photographe, Dj, retraité...)</span></label>
              <input type="text" name="situation_professionnelle" value={formData.situation_professionnelle} onChange={handleChange} />
            </div>

            <h2>Adresse</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Type d'adresse</label>
                <select name="type_adresse" value={formData.type_adresse} onChange={handleChange}>
                  <option value="domicile">Domicile</option>
                  <option value="entreprise">Entreprise</option>
                </select>
              </div>
            </div>
            {formData.type_adresse === 'entreprise' && (
              <div className="form-row">
                <div className="form-group">
                  <label>Nom du lieu</label>
                  <input type="text" name="nom_adresse" value={formData.nom_adresse} onChange={handleChange} />
                </div>
              </div>
            )}
            <div className="form-group">
              <label>Adresse</label>
              <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Complément d'adresse</label>
              <input type="text" name="adresse_complementaire" value={formData.adresse_complementaire} onChange={handleChange} />
            </div>
            <div className="form-row form-row-3">
              <div className="form-group form-group-sm">
                <label>CP</label>
                <input type="text" name="cp" value={formData.cp} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Ville</label>
                <input type="text" name="ville" value={formData.ville} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Pays</label>
                <select name="pays_id" value={formData.pays_id} onChange={handleChange}>
                  {lookups.pays.map(p => (
                    <option key={p.id} value={p.id}>{p.name_fr}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-group">
              <label>Distance approximative du centre ville de la ville principale</label>
              <input type="text" name="distance" value={formData.distance} onChange={handleChange} placeholder="ex: 5 km" />
            </div>

            {hasLocation && (
              <div className="form-map-preview">
                <label>Localisation</label>
                <div className="form-map-container">
                  <MapContainer center={position} zoom={13} scrollWheelZoom={false}>
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                    <Marker position={position} />
                  </MapContainer>
                </div>
              </div>
            )}

            <h2>Statut</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Raison sociale</label>
                <input type="text" name="raison_sociale" value={formData.raison_sociale} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Siret</label>
                <input type="text" name="siret" value={formData.siret} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Type de statut</label>
                <select name="type_statu_id" value={formData.type_statu_id} onChange={handleChange}>
                  <option value="">Sélectionner</option>
                  {lookups.typeStatus.map(ts => (
                    <option key={ts.id} value={ts.id}>{ts.nom}</option>
                  ))}
                </select>
              </div>
            </div>

            <h2>Profil</h2>
            <div className="form-row">
              <div className="form-group">
                <label>Photo de profil</label>
                <div className="photo-upload-area">
                  {formData.photo_profil ? (
                    <div className="photo-preview">
                      <img src={getUploadsUrl(formData.photo_profil)} alt="Photo de profil" />
                      <button type="button" className="btn-remove-photo" onClick={() => setFormData(prev => ({ ...prev, photo_profil: '' }))}>
                        ×
                      </button>
                    </div>
                  ) : (
                    <div className="photo-placeholder" onClick={() => fileInputRef.current?.click()}>
                      <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                        <circle cx="8.5" cy="8.5" r="1.5" />
                        <polyline points="21 15 16 10 5 21" />
                      </svg>
                      <span>{uploadingPhoto ? 'Upload...' : 'Cliquer pour ajouter'}</span>
                    </div>
                  )}
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handlePhotoUpload}
                    style={{ display: 'none' }}
                  />
                </div>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>État</label>
                <select name="etat" value={formData.etat} onChange={handleChange}>
                  <option value="actif">Actif</option>
                  <option value="inactif">Inactif</option>
                </select>
              </div>
              <div className="form-group">
                <label>Ville principale</label>
                {(() => {
                  // Si l'antenne du contact n'est pas (ou plus) dans le lookup, on
                  // injecte une option fantome pour que la dropdown affiche quand
                  // meme la ville actuelle (recuperee depuis contact.antenne_ville
                  // via formData.__antenne_ville_fallback). Sinon le select reste
                  // sur "Sélectionner" alors qu'une antenne_id est bien presente.
                  const currentId = String(formData.antenne_id || '');
                  const knownIds = new Set(lookups.antennes.map((a) => String(a.id)));
                  const missingFromLookup = currentId && !knownIds.has(currentId);
                  return (
                    <select name="antenne_id" value={currentId} onChange={handleChange}>
                      <option value="">Sélectionner</option>
                      {missingFromLookup && formData.__antenne_ville_fallback && (
                        <option value={currentId}>{formData.__antenne_ville_fallback}</option>
                      )}
                      {lookups.antennes.map(a => (
                        <option key={a.id} value={String(a.id)}>{a.ville_principale}</option>
                      ))}
                    </select>
                  );
                })()}
              </div>
              <div className="form-group">
                <label>Type de profil <span className="label-hint">(sélection multiple)</span></label>
                <select
                  name="type_profil_ids"
                  multiple
                  value={formData.type_profil_ids}
                  onChange={handleTypeProfilChange}
                  className="multi-select"
                >
                  {lookups.typeProfils.map(tp => (
                    <option key={tp.id} value={tp.id}>{tp.nom}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Qualification</label>
                <select name="qualification" value={formData.qualification} onChange={handleChange}>
                  <option value="">Sélectionner</option>
                  {lookups.qualifications.map(q => (
                    <option key={q.value} value={q.value}>{q.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>LinkedIn</label>
                <input type="url" name="linkedin" value={formData.linkedin} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Site internet</label>
                <input type="url" name="site_internet" value={formData.site_internet} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>ID Trello</label>
                <input type="text" name="id_trello" value={formData.id_trello} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>Travaille avec Selfizee depuis</label>
                <input type="date" name="travaille_depuis" value={formData.travaille_depuis} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Comment est-il rentré dans le réseau Selfizee</label>
                <select name="rentrer_selfizee" value={formData.rentrer_selfizee} onChange={handleChange}>
                  <option value="">Sélectionner</option>
                  {RENTRER_SELFIZEE_OPTIONS.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Précisions</label>
                <input type="text" name="precisions_rentrer_selfizee" value={formData.precisions_rentrer_selfizee} onChange={handleChange} placeholder="Précisez comment il est rentré dans le réseau" />
              </div>
            </div>
            <div className="form-row checkboxes">
              <label className="checkbox-label checkbox-warning">
                <input type="checkbox" name="ne_plus_contacter" checked={formData.ne_plus_contacter} onChange={handleChange} />
                <span>Ne plus contacter</span>
              </label>
            </div>

            <h2>Information comptable</h2>
            <div className="form-row">
              <div className="form-group">
                <label>IBAN</label>
                <input type="text" name="iban" value={formData.iban} onChange={handleChange} />
              </div>
              <div className="form-group">
                <label>BIC</label>
                <input type="text" name="bic" value={formData.bic} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Code comptable fournisseur</label>
                <input type="text" name="code_comptable_fournisseur" value={formData.code_comptable_fournisseur} onChange={handleChange} />
              </div>
            </div>
            <div className="form-row checkboxes">
              <label className="checkbox-label">
                <input type="checkbox" name="iban_added_in_edi" checked={formData.iban_added_in_edi} onChange={handleChange} />
                <span>Iban ajouté à l'EDI</span>
              </label>
            </div>
          </div>
        )}

        {activeStep === 2 && (
          <div className="step-content">
            <h2>Informations interne</h2>

            <div className="form-group">
              <label className="label-large">Bon à savoir</label>
              <p className="field-description">Précisez ici : les dispo / indispo, informations pratiques, moyen de communication, organisation, description de l'antenne …</p>
              <WysiwygField value={formData.bon_savoir} onChange={(html) => setFormData(prev => ({ ...prev, bon_savoir: html }))} rows={5} />
            </div>

            {/* Compétences annexes — propres au contact (déplacées du tarif). */}
            <div className="form-group">
              <label className="label-large">Compétences</label>
              <p className="field-description">Ce que le contact peut proposer en plus de la prestation de borne.</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {(() => {
                  // Adapte les libelles "Technicien" et "Animateur" au feminin
                  // si la civilite n est pas masculine (M / Mr / male / m).
                  const civ = String(formData.civilite || '').trim().toLowerCase();
                  const isMale = ['m', 'mr', 'male'].includes(civ);
                  return [
                    { key: 'photographie', label: 'Photographie' },
                    { key: 'animateur', label: isMale ? 'Animateur' : 'Animatrice' },
                    { key: 'technicien', label: isMale ? 'Technicien sur place' : 'Technicienne sur place' },
                    { key: 'depannage_info', label: 'Dépannage informatique' },
                    { key: 'deplacements_specifiques', label: 'Déplacements spécifiques' },
                  ];
                })().map((c) => {
                  const val = formData.competences?.[c.key] || {};
                  const setVal = (next) => setFormData((p) => ({ ...p, competences: { ...(p.competences || {}), [c.key]: next } }));
                  return (
                    <div key={c.key} style={{ display: 'grid', gridTemplateColumns: '180px auto 1fr', gap: 10, alignItems: 'center' }}>
                      <span style={{ fontSize: 13, color: '#475569' }}>{c.label}</span>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#64748b', cursor: 'pointer' }}>
                        <input type="checkbox" checked={!!val.actif} onChange={(e) => setVal({ ...val, actif: e.target.checked })} style={{ width: 14, height: 14, accentColor: '#6366f1', cursor: 'pointer' }} />
                        Oui
                      </label>
                      <input type="text" value={val.commentaire || ''} onChange={(e) => setVal({ ...val, commentaire: e.target.value })} placeholder="Commentaire (optionnel)" style={{ padding: '6px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 12, width: '100%' }} />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Notations — propres au contact (déplacées du tarif). */}
            <div className="form-group">
              <label className="label-large">Notations</label>
              <p className="field-description">Appréciation qualitative (1 à 5 étoiles).</p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {[
                  { key: 'flexibilite', label: 'Flexibilité' },
                  { key: 'reactivite', label: 'Réactivité / dépannage' },
                ].map((n) => {
                  const value = Number(formData.notations?.[n.key] || 0);
                  const setVal = (v) => setFormData((p) => ({ ...p, notations: { ...(p.notations || {}), [n.key]: v } }));
                  return (
                    <div key={n.key} style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
                      <span style={{ fontSize: 13, color: '#475569', minWidth: 200 }}>{n.label}</span>
                      <div style={{ display: 'inline-flex', gap: 4 }}>
                        {[1, 2, 3, 4, 5].map((star) => (
                          <button key={star} type="button" onClick={() => setVal(value === star ? 0 : star)} style={{ border: 'none', background: 'transparent', cursor: 'pointer', padding: 2, fontSize: 26, lineHeight: 1, color: star <= value ? '#fbbf24' : '#cbd5e1' }} title={`${star} étoile${star > 1 ? 's' : ''}`}>★</button>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Stockage — propre au contact (déplacé du tarif). */}
            <div className="form-group">
              <label className="label-large">Stockage</label>
              <p className="field-description">Possibilité de stocker du matériel chez ce contact.</p>
              <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 10, alignItems: 'center' }}>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={!!formData.stockage?.actif} onChange={(e) => setFormData((p) => ({ ...p, stockage: { ...(p.stockage || {}), actif: e.target.checked } }))} style={{ width: 14, height: 14, accentColor: '#6366f1' }} />
                  Propose du stockage
                </label>
                <input type="text" value={formData.stockage?.commentaire || ''} onChange={(e) => setFormData((p) => ({ ...p, stockage: { ...(p.stockage || {}), commentaire: e.target.value } }))} placeholder="Commentaire (volume, conditions, etc.)" style={{ padding: '6px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 12, width: '100%' }} />
              </div>
            </div>

            <div className="form-group">
              <label className="label-large">Informations de facturation</label>
              {/* Un seul jeu de radios : le tarif de base, puis les autres
                  grilles actives, puis le tarif spécifique en bas de liste.
                  Choisir une grille — base comprise — laisse la fiche SUIVRE
                  cette grille et ses évolutions ; seul le tarif spécifique la
                  détache. */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 6, marginTop: 4, marginBottom: 8 }}>
                {grillesProposables.map((g) => {
                  const coche = formData.tarif_mode === 'base'
                    && (g.par_defaut ? !formData.tarif_grille_id : formData.tarif_grille_id === g.id);
                  return (
                    <label key={g.id} style={styleRadio}>
                      <input
                        type="radio"
                        name="tarif_mode"
                        checked={coche}
                        onChange={() => setFormData((p) => ({
                          ...p,
                          tarif_mode: 'base',
                          tarif_grille_id: g.par_defaut ? null : g.id,
                        }))}
                        style={styleRadioInput}
                      />
                      <span>
                        Appliquer {g.libelle}
                        {g.par_defaut && <em style={styleMention}> — tarif de base</em>}
                        {g.statut === 'inactif' && <em style={{ ...styleMention, color: '#b45309' }}> — grille inactive</em>}
                      </span>
                    </label>
                  );
                })}
                <label style={styleRadio}>
                  <input
                    type="radio"
                    name="tarif_mode"
                    value="specifique"
                    checked={formData.tarif_mode === 'specifique'}
                    onChange={() => setFormData((p) => ({ ...p, tarif_mode: 'specifique' }))}
                    style={styleRadioInput}
                  />
                  Appliquer un tarif spécifique
                </label>
              </div>

              {formData.tarif_mode === 'base' && (
                <>
                  <p className="field-description">
                    Tarif appliqué :{' '}
                    <strong>{grilleAppliquee ? grilleAppliquee.libelle : 'grille par défaut'}</strong>
                    {grilleAppliquee && !grilleAppliquee.par_defaut
                      ? ' — grille partagée, ce n’est pas le tarif de base.'
                      : '.'}
                  </p>
                  <p className="field-description">
                    Ses évolutions s’appliquent automatiquement à cette fiche.
                    Consultez-la en lecture seule via le bouton ci-dessous.
                    {!isEdit && ' (Disponible après création du contact.)'}
                  </p>
                  {isEdit ? (
                    <button
                      type="button"
                      onClick={() => setShowTarifsModal({ mode: 'readonly' })}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 8,
                        border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                      }}
                    >
                      <TagIcon size={14} /> Voir le tarif appliqué
                    </button>
                  ) : (
                    <div style={{ padding: '12px 14px', background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 8, fontSize: 12, color: '#64748b' }}>
                      Crée d'abord le contact pour pouvoir consulter le tarif appliqué.
                    </div>
                  )}
                </>
              )}

              {formData.tarif_mode === 'specifique' && (
                <>

                  {/* Point de départ du tarif négocié. Une grille désactivée
                      n'est plus proposée, sauf si cette fiche l'utilise déjà :
                      on ne la re-tarife pas dans son dos. */}
                  <div style={{ marginBottom: 12 }}>
                    <label className="field-description" style={{ display: 'block', marginBottom: 6 }}>
                      Grille de départ (reprise de votre choix ci-dessus)
                    </label>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                      {grillesProposables
                        .map((g) => {
                          const choisi = g.par_defaut
                            ? !formData.tarif_grille_id || formData.tarif_grille_id === g.id
                            : formData.tarif_grille_id === g.id;
                          return (
                            <button
                              key={g.id}
                              type="button"
                              onClick={() => setFormData((p) => ({
                                ...p,
                                tarif_grille_id: g.par_defaut ? null : g.id,
                              }))}
                              style={{
                                padding: '8px 12px', borderRadius: 8, cursor: 'pointer',
                                border: '1px solid ' + (choisi ? '#4f46e5' : '#d9d9d9'),
                                background: choisi ? '#eef2ff' : 'white',
                                color: choisi ? '#4338ca' : '#475569',
                                fontSize: 13, fontWeight: choisi ? 700 : 500,
                                textAlign: 'left',
                              }}
                            >
                              {g.libelle}
                              {g.par_defaut && (
                                <span style={{ display: 'block', fontSize: 11, fontWeight: 500, opacity: 0.8 }}>
                                  tarif de base
                                </span>
                              )}
                              {g.statut === 'inactif' && (
                                <span style={{ display: 'block', fontSize: 11, fontWeight: 500, color: '#b45309' }}>
                                  grille inactive
                                </span>
                              )}
                            </button>
                          );
                        })}
                    </div>
                    <p className="field-description" style={{ marginTop: 6 }}>
                      Les montants de cette grille servent de point de départ : ce contact aura
                      un tarif <strong>négocié</strong>, propre à lui. Il ne suivra plus les
                      évolutions de la grille choisie.
                    </p>
                  </div>

                  <p className="field-description">
                    Tarif spécifique : la grille s'ouvre en édition. Seules les lignes que vous modifiez
                    sont sauvegardées en base (les autres restent héritées du standard).
                    {!isEdit && ' (Disponible après création du contact.)'}
                  </p>

                  {/* Liste des tarifs modifies (vs standard) — affichee juste
                      au-dessus du bouton, uniquement en mode edit et s'il y a
                      au moins un override. */}
                  {isEdit && tarifOverrides.length > 0 && (
                    <div style={{ marginBottom: 10, border: '1px solid #fde68a', background: '#fffbeb', borderRadius: 8, padding: '10px 12px' }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: 0.04, marginBottom: 6 }}>
                        Tarifs modifiés pour ce contact ({tarifOverrides.length})
                      </div>
                      <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13 }}>
                        {tarifOverrides.map((o, i) => (
                          <li key={i} style={{ marginBottom: 3 }}>
                            <span style={{ color: '#475569' }}>{o.label} : </span>
                            <strong style={{ color: '#92400e' }}>{fmtEuro(o.current)}</strong>
                            <span style={{ color: '#94a3b8' }}> (standard {fmtEuro(o.std)})</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isEdit && tarifOverrides.length === 0 && (
                    <p className="field-description" style={{ marginTop: 0, marginBottom: 10 }}>
                      Aucune ligne tarifaire modifiée pour l'instant. Cliquez ci-dessous pour personnaliser.
                    </p>
                  )}

                  {isEdit && (
                    <button
                      type="button"
                      onClick={() => setShowTarifsModal({ mode: 'edit' })}
                      style={{
                        display: 'inline-flex', alignItems: 'center', gap: 6,
                        padding: '8px 14px', borderRadius: 8,
                        border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca',
                        fontSize: 13, fontWeight: 600, cursor: 'pointer',
                        marginBottom: 10,
                      }}
                    >
                      <TagIcon size={14} /> Voir / modifier le tarif
                    </button>
                  )}
                  <label style={{ display: 'block', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                    Notes libres sur le tarif spécifique
                  </label>
                  <WysiwygField value={formData.information_facturation} onChange={(html) => setFormData(prev => ({ ...prev, information_facturation: html }))} rows={4} />
                </>
              )}
            </div>

            {/* Contact prioritaire — champ simple, pas de cartouche flashy */}
            <div className="form-group">
              <label className="label-large">Contact prioritaire</label>
              <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, cursor: 'pointer', marginTop: 4 }}>
                <input
                  type="checkbox"
                  checked={!!formData.prioritaire}
                  onChange={(e) => setFormData((p) => ({ ...p, prioritaire: e.target.checked }))}
                  style={{ width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer' }}
                />
                <span style={{ fontSize: 13, color: '#475569' }}>
                  À traiter en priorité (incidents, urgences, demandes spéciales).
                </span>
              </label>
            </div>

            {/* Contacts liés (0 à N) */}
            <div className="form-group">
              <label className="label-large">Contacts liés</label>
              <p className="field-description">Personnes / contacts associés à cette antenne (nom, rôle, téléphone…). Ajoutez autant de lignes que nécessaire.</p>
              {(formData.contacts_lies || []).map((cl, idx) => (
                <div key={idx} style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
                  <input
                    type="text"
                    value={cl}
                    onChange={(e) => {
                      const next = [...formData.contacts_lies];
                      next[idx] = e.target.value;
                      setFormData((p) => ({ ...p, contacts_lies: next }));
                    }}
                    placeholder="Ex: Jean Dupont — Conjoint — 06 12 34 56 78"
                    style={{ flex: 1, padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13 }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      const next = formData.contacts_lies.filter((_, i) => i !== idx);
                      setFormData((p) => ({ ...p, contacts_lies: next }));
                    }}
                    title="Retirer ce contact"
                    style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', color: '#dc2626', cursor: 'pointer', fontSize: 12 }}
                  >Retirer</button>
                </div>
              ))}
              <button
                type="button"
                onClick={() => setFormData((p) => ({ ...p, contacts_lies: [...(p.contacts_lies || []), ''] }))}
                style={{ marginTop: 4, padding: '6px 12px', borderRadius: 6, border: '1px solid #c7d2fe', background: '#eef2ff', color: '#4338ca', cursor: 'pointer', fontSize: 12 }}
              >+ Ajouter un contact lié</button>
            </div>

            {/* Préférence de livraison (0 à 1) */}
            <div className="form-group">
              <label className="label-large">Préférence de livraison</label>
              <p className="field-description">Précisez ici les préférences (créneau, point de remise, instructions de livraison…). Optionnel.</p>
              <textarea
                rows={3}
                value={formData.preference_livraison}
                onChange={(e) => setFormData((p) => ({ ...p, preference_livraison: e.target.value }))}
                placeholder="Ex: livrer le matin avant 11h, point de retrait commerce voisin…"
                style={{ width: '100%', padding: '8px 10px', border: '1px solid #d9d9d9', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', resize: 'vertical' }}
              />
            </div>

            {/* Véhicules (0 à N) */}
            <div className="form-group">
              <label className="label-large">Véhicule(s)</label>
              <p className="field-description">
                Véhicules du contact. Marques et modèles auto-complétés via NHTSA (USA) — la saisie manuelle reste possible si le véhicule n'est pas dans la liste.
              </p>
              <VehiculesList ref={vehiculesRef} contactId={id} />
            </div>

            <h2>Informations publiques</h2>
            <div className="info-notice">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="16" x2="12" y2="12" />
                <line x1="12" y1="8" x2="12.01" y2="8" />
              </svg>
              <span>Informations accessibles pour nos clients. Merci de rédiger correctement et sans faute d'orthographe.</span>
            </div>
            <div className="form-group">
              <label>Précisions sur le lieu (accessibilité, description…)</label>
              <p className="field-hint">Il s'agit d'une maison individuelle, merci de vous garer dans l'allée directement</p>
              <WysiwygField value={formData.precisions_lieu} onChange={(html) => setFormData(prev => ({ ...prev, precisions_lieu: html }))} rows={3} />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Préférences d'horaires de pickup retrait</label>
                <WysiwygField value={formData.preferences_horaires_pickup_retrait} onChange={(html) => setFormData(prev => ({ ...prev, preferences_horaires_pickup_retrait: html }))} rows={2} />
              </div>
              <div className="form-group">
                <label>Préférences d'horaires de pickup retour</label>
                <WysiwygField value={formData.preferences_horaires_pickup_retour} onChange={(html) => setFormData(prev => ({ ...prev, preferences_horaires_pickup_retour: html }))} rows={2} />
              </div>
            </div>

            <h2>Livraison</h2>
            <div className="form-group">
              <div className="radio-group-inline">
                <span className="radio-group-label">L'antenne souhaite livrer les clients pour un forfait unique de 90 € dans une zone prédéfinie</span>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="accept_delivery"
                      checked={formData.accept_delivery === true}
                      onChange={() => setFormData(prev => ({ ...prev, accept_delivery: true }))}
                    />
                    <span>OUI</span>
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="accept_delivery"
                      checked={formData.accept_delivery === false}
                      onChange={() => setFormData(prev => ({ ...prev, accept_delivery: false }))}
                    />
                    <span>NON</span>
                  </label>
                </div>
              </div>
            </div>
            {formData.accept_delivery && (
              <div className="form-group">
                <label>Zone de livraison</label>
                <textarea name="delivery_area" value={formData.delivery_area} onChange={handleChange} rows="3" placeholder="Décrivez la zone de livraison..." />
              </div>
            )}
          </div>
        )}

        {activeStep === 3 && (
          <div className="step-content">
            {isEdit ? (
              <>
                <div className="step-header-with-action">
                  <h2>Documents</h2>
                  <ActionMenu
                    items={[
                      { label: '+ Ajouter un document', onClick: () => setShowDocModal(true) },
                    ]}
                  />
                </div>
                {documents.length > 0 ? (
                  <div className="cf-documents-grid">
                    {documents.map(doc => {
                      const ext = (doc.fichier || '').split('.').pop().toLowerCase();
                      const isImg = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp'].includes(ext);
                      return (
                        <div key={doc.id} className="cf-document-card">
                          <div className="cf-doc-thumb">
                            {isImg ? (
                              <img src={getUploadsUrl(doc.fichier)} alt={doc.nom || doc.fichier} />
                            ) : (
                              <div className={`cf-doc-ext cf-doc-ext-${ext}`}>
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                  <polyline points="14 2 14 8 20 8" />
                                </svg>
                                <span>{ext.toUpperCase()}</span>
                              </div>
                            )}
                          </div>
                          <div className="cf-doc-info">
                            <span className="cf-doc-type-badge">{doc.type_document_nom || 'Document'}</span>
                            <p className="cf-doc-name" title={doc.nom || doc.fichier}>{doc.nom || doc.fichier}</p>
                            {doc.created && <p className="cf-doc-date">{new Date(doc.created).toLocaleDateString('fr-FR')}</p>}
                          </div>
                          <div className="cf-doc-actions">
                            <ActionMenu
                              items={[
                                {
                                  label: 'Télécharger',
                                  href: getUploadsUrl(doc.fichier),
                                  download: true,
                                  icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" />
                                    </svg>
                                  ),
                                },
                                {
                                  label: 'Supprimer',
                                  danger: true,
                                  onClick: () => handleDeleteDocument(doc.id),
                                  icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                      <polyline points="3 6 5 6 21 6" /><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                    </svg>
                                  ),
                                },
                              ]}
                            />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <p className="help-text">Aucun document</p>
                )}
              </>
            ) : (
              <>
                <h2>Documents</h2>
                <p className="help-text">Les documents peuvent être ajoutés après la création du contact.</p>
              </>
            )}
          </div>
        )}

        {activeStep === 4 && (
          <div className="step-content">
            {isEdit ? (
              <>
                <div className="step-header-with-action">
                  <h2>Bornes contact</h2>
                  <ActionMenu
                    items={[
                      { label: '+ Ajouter une borne', onClick: openBorneModal },
                    ]}
                  />
                </div>
                {bornes.length > 0 ? (
                  <div className="cf-table-wrapper">
                    <table className="cf-table">
                      <thead>
                        <tr>
                          <th>Gamme</th>
                          <th>Numéro</th>
                          <th>Modèle</th>
                          <th>N° série</th>
                          <th>Date d'arrivée</th>
                          <th>Commentaire</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {bornes.map(borne => (
                          <tr key={borne.id}>
                            <td><span className={`cf-gamme-badge gamme-${(borne.gamme_nom || 'autre').toLowerCase()}`}>{borne.gamme_nom || 'Autre'}</span></td>
                            <td><strong>{borne.numero_formated || borne.numero}</strong></td>
                            <td>{borne.model_nom || '--'}</td>
                            <td>{borne.numero_serie || '--'}</td>
                            <td>{borne.modified ? new Date(borne.modified).toLocaleDateString('fr-FR') : '--'}</td>
                            <td>{borne.commentaire || '--'}</td>
                            <td>
                              <ActionMenu
                                items={[
                                  { label: 'Retirer', danger: true, onClick: () => handleUnassignBorne(borne) },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="help-text">Aucune borne assignée</p>
                )}

                <div className="step-header-with-action" style={{ marginTop: '2rem' }}>
                  <h2>Accessoires</h2>
                  <ActionMenu
                    items={[
                      { label: '+ Ajouter un accessoire', onClick: () => setShowAccessoireModal(true) },
                    ]}
                  />
                </div>
                {accessoires.length > 0 ? (
                  <div className="cf-table-wrapper">
                    <table className="cf-table">
                      <thead>
                        <tr>
                          <th>Type</th>
                          <th>Equipement</th>
                          <th>Date d'arrivée</th>
                          <th>Commentaire</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {accessoires.map(acc => (
                          <tr key={acc.id}>
                            <td><span className="cf-type-badge" style={acc.type_equipement_couleur ? { backgroundColor: acc.type_equipement_couleur, color: '#fff' } : {}}>{acc.type_equipement_nom}</span></td>
                            <td>{acc.equipement_valeur || '--'}</td>
                            <td>{acc.date_arrive ? new Date(acc.date_arrive).toLocaleDateString('fr-FR') : '--'}</td>
                            <td>{acc.commentaire || '--'}</td>
                            <td>
                              <ActionMenu
                                items={[
                                  { label: 'Supprimer', danger: true, onClick: () => handleDeleteAccessoire(acc.id) },
                                ]}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="help-text">Aucun accessoire</p>
                )}
              </>
            ) : (
              <>
                <h2>Matériel</h2>
                <p className="help-text">Le matériel peut être assigné après la création du contact.</p>
              </>
            )}
          </div>
        )}

        {activeStep === 5 && (
          <div className="step-content">
            {isEdit ? (
              <>
                <div className="step-header-with-action">
                  <h2>Binôme(s)</h2>
                  <ActionMenu
                    items={[
                      { label: '+ Ajouter un binôme', onClick: () => setShowBinomeModal(true) },
                    ]}
                  />
                </div>
                {binomes.length > 0 ? (
                  <div className="cf-binomes-grid">
                    {binomes.map(binome => (
                      <div key={binome.id} className="cf-binome-card">
                        {(canEditBinome || canDeleteBinome) && (
                          <div className="cf-binome-menu">
                            <ActionMenu
                              items={[
                                ...(canEditBinome ? [{ label: 'Modifier', onClick: () => handleEditBinome(binome) }] : []),
                                ...(canDeleteBinome ? [{ label: 'Supprimer la liaison', danger: true, onClick: () => setDeletingBinome(binome) }] : []),
                              ]}
                            />
                          </div>
                        )}
                        <div className="cf-binome-avatar">
                          {binome.binome_photo ? (
                            <img src={getUploadsUrl(binome.binome_photo)} alt={`${binome.binome_prenom} ${binome.binome_nom}`} />
                          ) : binome.binome_civilite === 'm' ? (
                            <img src="/img/avatar-homme.svg" alt="Avatar" />
                          ) : (
                            <img src="/img/avatar-femme.svg" alt="Avatar" />
                          )}
                        </div>
                        <div className="cf-binome-info">
                          <h4>{binome.binome_prenom} {binome.binome_nom}</h4>
                          {binome.binome_raison_sociale && <p className="cf-binome-company">{binome.binome_raison_sociale}</p>}
                          <p><strong>Lien :</strong> {binome.lien_binome_nom || 'Non précisé'}</p>
                          {binome.precisions && <p><strong>Précisions :</strong> {binome.precisions}</p>}
                          {binome.binome_tel && <p className="cf-binome-contact">{binome.binome_tel}</p>}
                          {binome.binome_email && <p className="cf-binome-contact">{binome.binome_email}</p>}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="help-text">Aucun binôme associé</p>
                )}
              </>
            ) : (
              <>
                <h2>Binôme(s)</h2>
                <p className="help-text">Les binômes peuvent être ajoutés après la création du contact.</p>
              </>
            )}
          </div>
        )}

        <div className="form-actions">
          {activeStep > 1 && (
            <button type="button" className="btn btn-secondary" onClick={() => setActiveStep(activeStep - 1)}>
              Précédent
            </button>
          )}
          {activeStep < 5 && (
            <button type="button" className="btn btn-primary" onClick={() => setActiveStep(activeStep + 1)}>
              Suivant
            </button>
          )}
          {activeStep === 5 && !isEdit && (
            <button type="submit" className="btn btn-success" disabled={saving}>
              {saving ? 'Enregistrement...' : 'Enregistrer et continuer'}
            </button>
          )}
        </div>
      </form>

      {/* Document Modal */}
      {showDocModal && (
        <div className="modal-overlay" onClick={() => setShowDocModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un document</h3>
              <button type="button" className="modal-close" onClick={() => setShowDocModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleAddDocument}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Fichier *</label>
                  <input ref={docFileRef} type="file" onChange={(e) => setDocFormData(prev => ({ ...prev, file: e.target.files[0] }))} required />
                </div>
                <div className="form-group">
                  <label>Type de document</label>
                  <select value={docFormData.type_document_id} onChange={(e) => setDocFormData(prev => ({ ...prev, type_document_id: e.target.value }))}>
                    <option value="">-- Sélectionner --</option>
                    {lookups.contactTypeDocuments.map(t => (
                      <option key={t.id} value={t.id}>{t.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Nom du document</label>
                  <input type="text" value={docFormData.nom} onChange={(e) => setDocFormData(prev => ({ ...prev, nom: e.target.value }))} placeholder="Optionnel" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDocModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={uploadingDoc}>{uploadingDoc ? 'Upload...' : 'Enregistrer'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Borne Modal */}
      {showBorneModal && (
        <div className="modal-overlay" onClick={() => setShowBorneModal(false)}>
          <div className="modal-content modal-with-search" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Assigner une borne</h3>
              <button type="button" className="modal-close" onClick={() => setShowBorneModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleAssignBorne}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Gamme</label>
                  <select value={borneFormData.gamme_borne_id} onChange={(e) => handleGammeChange(e.target.value)}>
                    <option value="">Toutes les gammes</option>
                    {lookups.gammesBornes.map(g => (
                      <option key={g.id} value={g.id}>{g.name}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Borne *</label>
                  <SearchSelect
                    placeholder="Rechercher une borne (numéro, modèle, n° de série)"
                    value={borneFormData.borne_id}
                    onChange={(val) => setBorneFormData(prev => ({ ...prev, borne_id: val }))}
                    options={bornesDisponibles.map(b => ({
                      value: String(b.id),
                      // Numéro (recomposé avec la gamme s'il manque) – gamme et modèle (n° de série)
                      label: [
                        b.numero_formated
                          || ((b.numero ? `${(b.gamme_nom || '').charAt(0).toUpperCase()}${b.numero}` : `Borne #${b.id}`) + ' ⚠ n° estimé'),
                        borneType(b),
                      ].filter(Boolean).join(' – ') + (b.numero_serie ? ` (${b.numero_serie})` : ''),
                      search: b.numero ? String(b.numero) : '',
                    }))}
                  />
                  {bornesDisponibles.length === 0 && (
                    <p className="field-hint">Aucune borne disponible{borneFormData.gamme_borne_id ? ' pour cette gamme' : ''}</p>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBorneModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={!borneFormData.borne_id}>Assigner</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Accessoire Modal */}
      {showAccessoireModal && (
        <div className="modal-overlay" onClick={() => setShowAccessoireModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un accessoire</h3>
              <button type="button" className="modal-close" onClick={() => setShowAccessoireModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleAddAccessoire}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Type d'équipement *</label>
                  <select value={accFormData.type_equipement_id} onChange={(e) => handleTypeEquipementChange(e.target.value)}>
                    <option value="">-- Sélectionner --</option>
                    {lookups.typeEquipements.map(t => (
                      <option key={t.id} value={t.id}>{t.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Equipement</label>
                  <select value={accFormData.equipement_id} onChange={(e) => setAccFormData(prev => ({ ...prev, equipement_id: e.target.value }))} disabled={!accFormData.type_equipement_id}>
                    <option value="">-- Sélectionner --</option>
                    {equipementsList.map(e => (
                      <option key={e.id} value={e.id}>{e.valeur}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Date d'arrivée</label>
                  <input type="date" value={accFormData.date_arrive} onChange={(e) => setAccFormData(prev => ({ ...prev, date_arrive: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label>Commentaire</label>
                  <textarea value={accFormData.commentaire} onChange={(e) => setAccFormData(prev => ({ ...prev, commentaire: e.target.value }))} rows="2" placeholder="Optionnel" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowAccessoireModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Binome liaison Modal */}
      {editingBinome && (
        <div className="modal-overlay" onClick={() => setEditingBinome(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Modifier la liaison binôme</h3>
              <button type="button" className="modal-close" onClick={() => setEditingBinome(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleUpdateBinome}>
              <div className="modal-body">
                <p style={{ fontSize: 13, color: '#475569', marginBottom: 12 }}>
                  Binôme : <strong>{editingBinome.binome_prenom} {editingBinome.binome_nom}</strong>
                </p>
                <div className="form-group">
                  <label>Type de lien</label>
                  <select
                    value={editBinomeData.lien_binome_id}
                    onChange={(e) => setEditBinomeData(prev => ({ ...prev, lien_binome_id: e.target.value }))}
                  >
                    <option value="">-- Sélectionner --</option>
                    {lookups.lienBinomes.map(lien => (
                      <option key={lien.id} value={lien.id}>{lien.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Précisions</label>
                  <textarea
                    value={editBinomeData.precisions}
                    onChange={(e) => setEditBinomeData(prev => ({ ...prev, precisions: e.target.value }))}
                    rows="3"
                    placeholder="Ajouter des précisions sur cette liaison..."
                  />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setEditingBinome(null)}>Annuler</button>
                <button type="submit" className="btn btn-primary">Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Binome liaison confirmation Modal */}
      {deletingBinome && (
        <div className="modal-overlay" onClick={() => setDeletingBinome(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Supprimer la liaison</h3>
              <button type="button" className="modal-close" onClick={() => setDeletingBinome(null)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 14, color: '#475569' }}>
                Êtes-vous sûr de vouloir supprimer la liaison avec <strong>{deletingBinome.binome_prenom} {deletingBinome.binome_nom}</strong> ?
              </p>
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 6 }}>
                Le contact binôme n'est pas supprimé, seule la liaison entre les deux contacts l'est.
              </p>
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setDeletingBinome(null)}>Annuler</button>
              <button type="button" className="btn btn-danger" onClick={confirmDeleteBinome}>Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Binome Modal */}
      {showBinomeModal && (
        <div className="modal-overlay" onClick={() => setShowBinomeModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Ajouter un binôme</h3>
              <button type="button" className="modal-close" onClick={() => setShowBinomeModal(false)}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <form onSubmit={handleAddBinome}>
              <div className="modal-body">
                <div className="form-group">
                  <label>Binôme *</label>
                  {binomeFormData.binome_id ? (
                    <div className="cf-selected-contact">
                      <span>{binomeFormData.binome_nom}</span>
                      <button type="button" onClick={() => setBinomeFormData(prev => ({ ...prev, binome_id: '', binome_nom: '' }))}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
                      </button>
                    </div>
                  ) : (
                    <div className="cf-contact-search">
                      <input
                        type="text"
                        value={contactSearch}
                        onChange={(e) => handleContactSearch(e.target.value)}
                        placeholder="Rechercher un contact par nom..."
                      />
                      {searchingContacts && <p className="field-hint">Recherche...</p>}
                      {contactResults.length > 0 && (
                        <div className="cf-contact-results">
                          {contactResults.map(c => (
                            <button key={c.id} type="button" className="cf-contact-result-item" onClick={() => handleSelectBinomeContact(c)}>
                              {c.prenom} {c.nom} {c.ville ? `- ${c.ville}` : ''}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div className="form-group">
                  <label>Type de lien</label>
                  <select value={binomeFormData.lien_binome_id} onChange={(e) => setBinomeFormData(prev => ({ ...prev, lien_binome_id: e.target.value }))}>
                    <option value="">-- Sélectionner --</option>
                    {lookups.lienBinomes.map(l => (
                      <option key={l.id} value={l.id}>{l.nom}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Précisions</label>
                  <textarea value={binomeFormData.precisions} onChange={(e) => setBinomeFormData(prev => ({ ...prev, precisions: e.target.value }))} rows="2" placeholder="Optionnel" />
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowBinomeModal(false)}>Annuler</button>
                <button type="submit" className="btn btn-primary" disabled={!binomeFormData.binome_id}>Enregistrer</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Unassign Borne Modal */}
      {confirmUnassign && (
        <div className="modal-overlay" onClick={() => !unassigning && setConfirmUnassign(null)}>
          <div className="modal-content modal-confirm" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Retirer la borne</h3>
              <button
                type="button"
                className="modal-close"
                onClick={() => setConfirmUnassign(null)}
                disabled={unassigning}
              >
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>
            <div className="modal-body">
              <div className="confirm-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
                  <line x1="12" y1="9" x2="12" y2="13" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <p className="confirm-message">
                Êtes-vous sûr de vouloir retirer la borne{' '}
                <strong>{confirmUnassign.numero_formated || confirmUnassign.numero}</strong>{' '}
                de ce contact&nbsp;?
              </p>
              <p className="confirm-sub">Cette action peut être annulée en réassignant la borne.</p>
            </div>
            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setConfirmUnassign(null)}
                disabled={unassigning}
              >
                Annuler
              </button>
              <button
                type="button"
                className="btn btn-danger"
                onClick={confirmUnassignBorne}
                disabled={unassigning}
              >
                {unassigning ? 'Retrait…' : 'Retirer la borne'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showTarifsModal && isEdit && (
        <ContactTarifsModal
          contactId={parseInt(id, 10)}
          contactName={`${formData.prenom || ''} ${formData.nom || ''}`.trim() || formData.raison_sociale || ''}
          forceReadonly={showTarifsModal?.mode === 'readonly'}
          onClose={() => { setShowTarifsModal(false); setTarifsReloadKey((k) => k + 1); }}
        />
      )}

      {confirmSuppression && (
        <ConfirmModal
          titre="Supprimer ce contact ?"
          message="La fiche sera retirée de la liste et placée dans la corbeille."
          detail="Elle reste consultable, exportable et restaurable depuis la corbeille. La suppression définitive s'y fait dans un second temps."
          libelleConfirmer="Supprimer"
          danger
          busy={suppressionEnCours}
          onClose={() => setConfirmSuppression(false)}
          onConfirm={async () => {
            setSuppressionEnCours(true);
            try {
              await deleteContact(id);
              toast.success('Contact placé dans la corbeille');
              navigate('/contacts');
            } catch (err) {
              toast.error('Erreur lors de la suppression');
            } finally {
              setSuppressionEnCours(false);
              setConfirmSuppression(false);
            }
          }}
        />
      )}
    </div>
  );
}

export default ContactForm;
